import { createApp } from 'vue';
import axios from 'axios';
import VueClipboard from 'vue-clipboard2';
import { ApplicationInsights } from '@microsoft/applicationinsights-web';

import router from './core/router/router';
import store from './core/store/store';
import i18n from './core/translations/i18n';

import initCoreUi from './views/components/coreui';
import PulinetDataTable from './views/components/PulinetDataTable/PulinetDataTable.vue';
import initElementUI from './core/config/config-elementui';
import initFontAwesome from './core/config/config-fa';

import App from './App.vue';
import tilesClient from './core/api/tiles-service';
import { configureGuards } from './core/router/guards';
import { READ_TOKEN } from './core/store/types/action-types';

/**
 * Avvio dell'applicazione.
 *
 * La forma resta quella di prima - si scarica `config.json` e solo dopo si monta - perche la
 * configurazione qui e a **runtime**: l'indirizzo dell'API, il Tileserver e la chiave di telemetria
 * arrivano da un file servito accanto agli artefatti, non da variabili di build. E il motivo per cui
 * il deploy resta configurabile dopo la compilazione, e per cui la migrazione a Vite non ha dovuto
 * rinominare una sola variabile d'ambiente: non ce ne sono.
 */

store.dispatch(READ_TOKEN);

const loco = window.location;
// `BASE_URL` termina gia con `/`, quindi l'URL risultante contiene un doppio slash. Era cosi anche
// con Vue CLI e funziona: non lo si corregge in questo passo, per non mescolare due cambiamenti.
const appRootUrl = `${loco.protocol}//${loco.host}${import.meta.env.BASE_URL}`;
const configUrl = `${appRootUrl}/config.json`;

axios
  .get(configUrl)
  .then((r) => {
    tilesClient.setBaseUrl(r.data.TILESERVER_URL);
    (window as any).vue$config = r.data;
    axios.defaults.baseURL = r.data.API_ENDPOINT;
    configureGuards(router);

    const app = createApp(App);

    // 1. proprieta globali, prima che qualunque componente venga creato
    app.config.globalProperties.$config = r.data;

    // 2. librerie di componenti
    initCoreUi(app);
    initFontAwesome(app);
    initElementUI(app);

    // Registrata globalmente come lo era `data-tables-server` di `vue-data-tables`, cosi i 37 punti
    // d'uso non hanno bisogno di un import ciascuno.
    app.component('PulinetDataTable', PulinetDataTable);

    // 3. plugin applicativi. Il router per ultimo: il suo `install()` avvia la navigazione iniziale.
    app.use(VueClipboard);
    app.use(i18n);
    app.use(store);
    app.use(router);

    // 4. telemetria. `vue-application-insights` era Vue 2 soltanto - scriveva su `Vue.prototype` e
    //    chiamava `router.onReady()`, che in vue-router 4 non esiste piu. Si usa direttamente la
    //    libreria che quel plugin avvolgeva, che era gia in `node_modules` come sua dipendenza.
    if (r.data.APPINSIGHTS) {
      const appInsights = new ApplicationInsights({ config: { instrumentationKey: r.data.APPINSIGHTS } });
      appInsights.loadAppInsights();
      app.config.globalProperties.$appInsights = appInsights;

      const nomePagina = (nome: unknown) => `(Vue App) / ${String(nome)}`;
      router.beforeEach((to) => {
        appInsights.startTrackPage(nomePagina(to.name));
      });
      router.afterEach((to) => {
        appInsights.stopTrackPage(nomePagina(to.name), `${loco.protocol}//${loco.host}${to.fullPath}`);
        appInsights.flush();
      });
    }

    // 5. si monta solo a navigazione iniziale risolta: le guardie del router leggono lo store e
    //    possono deviare al login, e montare prima farebbe lampeggiare la pagina sbagliata.
    router.isReady().then(() => app.mount('#app'));
  })
  .catch((r) => {
    alert(`Error on loading app config ${r}`);
  });
